'use client';

import { Button } from '@ui/components/button';
import { text } from '@modules/garden-planner/text';

import { useEffect, useState } from 'react';
import { FaIcon } from '@micks-yard/icons/FaIcon';
import { faCheck } from '@micks-yard/icons/solid/faCheck';
import { faInfo } from '@micks-yard/icons/solid/faInfo';
import { faSpinner } from '@micks-yard/icons/solid/faSpinner';
import { faTriangleExclamation } from '@micks-yard/icons/solid/faTriangleExclamation';
import { faXmark } from '@micks-yard/icons/solid/faXmark';
import type { AlertConfig } from '@modules/garden-planner/models';

export type { AlertConfig };

interface AlertMessageProps {
  alerts: AlertConfig[];
  onDismiss: (id: string) => void;
}

export function AlertMessage({ alerts, onDismiss }: AlertMessageProps) {
  if (alerts.length === 0) return null;

  const getAlertBgClass = (type: AlertConfig['type']) => {
    switch (type) {
      case 'saving':
        return 'bg-blue-600';
      case 'success':
        return 'bg-green-600';
      case 'error':
        return 'bg-red-600';
      case 'info':
      default:
        return 'bg-gray-600';
    }
  };

  const getIcon = (type: AlertConfig['type']) => {
    switch (type) {
      case 'saving':
        return <FaIcon icon={faSpinner} className="text-base text-white" spin />;
      case 'success':
        return <FaIcon icon={faCheck} className="text-lg text-white" />;
      case 'error':
        return <FaIcon icon={faTriangleExclamation} className="text-lg text-white" />;
      case 'info':
        return <FaIcon icon={faInfo} className="text-lg text-white" />;
      default:
        return null;
    }
  };

  return (
    <div className="pointer-events-none fixed right-6 bottom-6 z-50 flex max-w-sm flex-col gap-3">
      {alerts.map((alert) => (
        <AlertItem
          key={alert.id}
          alert={alert}
          onDismiss={onDismiss}
          backgroundClassName={getAlertBgClass(alert.type)}
          icon={getIcon(alert.type)}
        />
      ))}
    </div>
  );
}

interface AlertItemProps {
  alert: AlertConfig;
  onDismiss: (id: string) => void;
  backgroundClassName: string;
  icon: React.ReactNode;
}

function AlertItem({ alert, onDismiss, backgroundClassName, icon }: AlertItemProps) {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (alert.type !== 'saving' && alert.duration) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(() => onDismiss(alert.id), 300);
      }, alert.duration);

      return () => clearTimeout(timer);
    }
  }, [alert, onDismiss]);

  return (
    <div
      className={`pointer-events-auto rounded-xl px-4 py-3 shadow-lg transition-all duration-300 ${backgroundClassName}${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'}`}>
      <div className="flex flex-row items-center gap-3">
        <div className="shrink-0">{icon}</div>
        <p className="text-sm font-medium text-white">{alert.message}</p>
        {alert.type === 'saving' ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            className="ml-auto shrink-0 text-white hover:bg-white/10 hover:text-white"
            onClick={() => {
              setIsVisible(false);
              setTimeout(() => onDismiss(alert.id), 300);
            }}
            aria-label={text.dismissAlert}>
            <FaIcon icon={faXmark} className="text-sm" />
          </Button>
        ) : null}
      </div>
    </div>
  );
}
